import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { motion } from 'framer-motion';
import {
  Play,
  Square,
  Activity,
  CheckCircle2,
  XCircle,
  Terminal,
  Clock,
  Database,
  Cpu,
  RefreshCw,
  Search,
  Eye,
} from 'lucide-react';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as ChartTooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';

const COUNT_PRESETS = [5, 10, 15, 20];

const InternalLoadTest = () => {
  const [screenings, setScreenings] = useState([]);
  const [screeningsLoading, setScreeningsLoading] = useState(false);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  // Form State
  const [selectedScreeningId, setSelectedScreeningId] = useState('');
  const [customScreeningId, setCustomScreeningId] = useState('');
  const [screeningSearch, setScreeningSearch] = useState('');
  const [candidateCount, setCandidateCount] = useState(5);
  const [staggerMs, setStaggerMs] = useState(0);
  const [simulateInterview, setSimulateInterview] = useState(true);
  const [simulateVideo, setSimulateVideo] = useState(false);
  const [liveInterview, setLiveInterview] = useState(false);

  // Active / Selected Run State
  const [activeLogId, setActiveLogId] = useState(null);
  const [selectedLog, setSelectedLog] = useState(null);
  const [selectedLogLoading, setSelectedLogLoading] = useState(false);
  const [launchLoading, setLaunchLoading] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);

  const terminalEndRef = useRef(null);

  // Auto-scroll terminal to bottom when new logs stream in
  useEffect(() => {
    if (terminalEndRef.current) {
      terminalEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [selectedLog?.terminalLogs]);

  // Load initial data
  const fetchData = async () => {
    setScreeningsLoading(true);
    setHistoryLoading(true);
    try {
      const [screeningsRes, historyRes] = await Promise.all([
        axios.get('/api/internal/load-test/screenings'),
        axios.get('/api/internal/load-test/history'),
      ]);
      setScreenings(screeningsRes.data.screenings || []);
      setHistory(historyRes.data.logs || []);

      // If there is an active running log in history, set it up for polling
      const runningLog = (historyRes.data.logs || []).find(
        (log) => log.status === 'running' || log.status === 'pending'
      );
      if (runningLog) {
        setActiveLogId(runningLog._id);
        fetchLogDetails(runningLog._id);
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to load dashboard data');
    } finally {
      setScreeningsLoading(false);
      setHistoryLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Poll details of active running log
  useEffect(() => {
    if (!activeLogId) return;

    const interval = setInterval(async () => {
      try {
        const res = await axios.get(`/api/internal/load-test/${activeLogId}`);
        const log = res.data.log;
        setSelectedLog(log);

        if (log.status !== 'running' && log.status !== 'pending') {
          setActiveLogId(null);
          clearInterval(interval);
          // Refresh history list to show updated status
          const historyRes = await axios.get('/api/internal/load-test/history');
          setHistory(historyRes.data.logs || []);
          toast.success(`Load test finished with status: ${log.status}`);
        }
      } catch (err) {
        console.error('Error polling load test:', err);
      }
    }, 2000);

    return () => clearInterval(interval);
  }, [activeLogId]);

  const fetchLogDetails = async (id) => {
    setSelectedLogLoading(true);
    try {
      const res = await axios.get(`/api/internal/load-test/${id}`);
      setSelectedLog(res.data.log);
    } catch (err) {
      toast.error('Failed to load logs');
    } finally {
      setSelectedLogLoading(false);
    }
  };

  const handleLaunchTest = async (e) => {
    e.preventDefault();
    const screeningId = customScreeningId.trim() || selectedScreeningId;
    if (!screeningId) {
      toast.error('Please select a screening or enter a custom Screening ID');
      return;
    }

    setLaunchLoading(true);
    try {
      const res = await axios.post('/api/internal/load-test/run', {
        screeningId,
        count: Number(candidateCount),
        stagger: Number(staggerMs),
        simulateInterview,
        simulateVideo,
        liveInterview,
      });

      toast.success(res.data.message || 'Load test launched successfully');
      setActiveLogId(res.data.logId);
      fetchLogDetails(res.data.logId);

      // Refresh history list
      const historyRes = await axios.get('/api/internal/load-test/history');
      setHistory(historyRes.data.logs || []);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to launch load test');
    } finally {
      setLaunchLoading(false);
    }
  };

  const handleCancelTest = async (id) => {
    if (!window.confirm('Are you sure you want to cancel the running load test?')) return;
    setCancelLoading(true);
    try {
      const res = await axios.post(`/api/internal/load-test/${id}/cancel`);
      toast.success(res.data.message || 'Load test cancelled');
      setActiveLogId(null);
      setSelectedLog(res.data.log);

      // Refresh history
      const historyRes = await axios.get('/api/internal/load-test/history');
      setHistory(historyRes.data.logs || []);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to cancel test');
    } finally {
      setCancelLoading(false);
    }
  };

  // Helper formatting functions
  const formatTime = (ms) => {
    if (ms == null) return '—';
    if (ms < 1000) return `${ms}ms`;
    return `${(ms / 1000).toFixed(2)}s`;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleString(undefined, {
      dateStyle: 'short',
      timeStyle: 'medium',
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'bg-emerald-50 text-emerald-700 border-emerald-200';
      case 'failed':
        return 'bg-rose-50 text-rose-700 border-rose-200';
      case 'running':
        return 'bg-blue-50 text-blue-700 border-blue-200 animate-pulse';
      case 'pending':
        return 'bg-amber-50 text-amber-700 border-amber-200';
      case 'cancelled':
      default:
        return 'bg-gray-100 text-gray-700 border-gray-200';
    }
  };

  const getStepIcon = (state) => {
    if (state === true) return <CheckCircle2 className="w-5 h-5 text-emerald-500" />;
    if (state === false) return <XCircle className="w-5 h-5 text-rose-500" />;
    if (state === null) return <span className="text-gray-400 font-mono text-xs">—</span>;
    return <Activity className="w-5 h-5 text-blue-500 animate-spin" />;
  };

  const filteredScreenings = screenings.filter((s) => {
    const search = screeningSearch.toLowerCase();
    const name = (s.name || '').toLowerCase();
    const job = (s.jobId?.title || '').toLowerCase();
    const company = (s.jobId?.companyName || s.organisationId?.name || '').toLowerCase();
    return name.includes(search) || job.includes(search) || company.includes(search);
  });

  // Prepare chart data
  const chartData = (selectedLog?.resourceSamples || []).map((sample) => {
    const sec = Math.round(sample.timeOffsetMs / 1000);
    return {
      time: `${sec}s`,
      CPU: sample.cpu,
      RAM: sample.ramPercentage,
      ramRaw: (sample.ram / 1024).toFixed(2), // GB
    };
  });

  return (
    <div className="w-full px-4 sm:px-6 lg:px-8 py-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-8 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-extrabold text-gray-900 tracking-tight flex items-center gap-2">
            <Activity className="text-blue-600 w-8 h-8" />
            Interview Load Testing
          </h1>
          <p className="mt-2 text-gray-600 text-sm sm:text-base max-w-3xl">
            Simulate concurrent candidates completing their screening application, uploading resumes,
            fetching LiveKit tokens, and performing Q&A interview rounds to benchmark server stress limits.
          </p>
        </div>
        <button
          type="button"
          onClick={fetchData}
          disabled={screeningsLoading || historyLoading}
          className="self-start inline-flex items-center gap-2 px-4 py-2.5 rounded-xl border border-gray-200 bg-white text-gray-700 font-medium hover:bg-gray-50 transition shadow-sm hover:shadow active:scale-95 disabled:opacity-50"
        >
          <RefreshCw className={`w-4 h-4 ${(screeningsLoading || historyLoading) ? 'animate-spin' : ''}`} />
          Refresh Data
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-start">
        {/* Left Side - Configurator Form */}
        <div className="lg:col-span-1 space-y-6">
          <div className="bg-white rounded-2xl border border-gray-200/80 shadow-sm overflow-hidden p-6">
            <h2 className="text-lg font-bold text-gray-900 mb-5 flex items-center gap-2">
              <Database className="w-5 h-5 text-gray-500" />
              Configure Simulation
            </h2>
            <form onSubmit={handleLaunchTest} className="space-y-5">
              {/* Screening Selection */}
              <div>
                <label className="block text-sm font-semibold text-gray-800 mb-2">
                  Select Screening
                </label>
                <div className="relative mb-3">
                  <span className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
                    <Search className="h-4 w-4 text-gray-400" />
                  </span>
                  <input
                    type="text"
                    placeholder="Search screenings, jobs, or orgs..."
                    value={screeningSearch}
                    onChange={(e) => setScreeningSearch(e.target.value)}
                    className="w-full rounded-xl border border-gray-200 bg-gray-50/50 pl-10 pr-4 py-2.5 text-sm text-gray-900 placeholder-gray-400 focus:border-blue-500 focus:bg-white focus:ring-2 focus:ring-blue-500/20 outline-none transition"
                  />
                </div>
                <select
                  value={selectedScreeningId}
                  onChange={(e) => {
                    setSelectedScreeningId(e.target.value);
                    setCustomScreeningId(''); // clear override
                  }}
                  className="w-full rounded-xl border border-gray-200 bg-white px-3 py-2.5 text-sm text-gray-700 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 outline-none transition"
                >
                  <option value="">-- Choose Screening --</option>
                  {filteredScreenings.map((s) => (
                    <option key={s._id} value={s._id}>
                      {s.jobId?.title || 'Unknown Job'} - {s.name} ({s.organisationId?.name || 'No Org'})
                    </option>
                  ))}
                </select>
              </div>

              {/* Custom Screening ID Override */}
              <div>
                <label className="block text-sm font-semibold text-gray-800 mb-1">
                  Or Custom Screening ID Override
                </label>
                <p className="text-[11px] text-gray-500 mb-1.5">
                  Input a direct mongoId if screening doesn't appear in list.
                </p>
                <input
                  type="text"
                  placeholder="e.g. 658cf41f84b5539ab426..."
                  value={customScreeningId}
                  onChange={(e) => {
                    setCustomScreeningId(e.target.value);
                    if (e.target.value) setSelectedScreeningId('');
                  }}
                  className="w-full rounded-xl border border-gray-200 bg-gray-50/50 px-4 py-2.5 text-sm text-gray-900 focus:border-blue-500 focus:bg-white focus:ring-2 focus:ring-blue-500/20 outline-none transition"
                />
              </div>

              {/* Candidate Count */}
              <div>
                <label className="block text-sm font-semibold text-gray-800 mb-2">
                  Concurrent Candidate Count
                </label>
                <div className="flex gap-2 mb-2">
                  {COUNT_PRESETS.map((preset) => (
                    <button
                      key={preset}
                      type="button"
                      onClick={() => setCandidateCount(preset)}
                      className={`flex-1 py-1.5 text-xs font-semibold rounded-lg border transition ${
                        candidateCount === preset
                          ? 'border-blue-500 bg-blue-50/80 text-blue-700'
                          : 'border-gray-200 bg-white text-gray-600 hover:bg-gray-50'
                      }`}
                    >
                      {preset} load
                    </button>
                  ))}
                </div>
                <input
                  type="number"
                  min="1"
                  max="50"
                  value={candidateCount}
                  onChange={(e) => setCandidateCount(Number(e.target.value))}
                  className="w-full rounded-xl border border-gray-200 bg-gray-50/50 px-4 py-2 text-sm text-gray-900 focus:border-blue-500 focus:bg-white focus:ring-2 focus:ring-blue-500/20 outline-none transition"
                />
              </div>

              {/* Stagger Delay */}
              <div>
                <label className="block text-sm font-semibold text-gray-800 mb-1.5">
                  Launch Stagger (ms)
                </label>
                <div className="flex items-center gap-3">
                  <input
                    type="range"
                    min="0"
                    max="10000"
                    step="500"
                    value={staggerMs}
                    onChange={(e) => setStaggerMs(Number(e.target.value))}
                    className="flex-1 h-1.5 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-600"
                  />
                  <span className="text-xs font-mono font-bold text-gray-700 w-16 text-right shrink-0">
                    {staggerMs > 0 ? `${(staggerMs / 1000).toFixed(1)}s` : 'Instant'}
                  </span>
                </div>
              </div>

              {/* Test Modes */}
              <div className="space-y-3.5 pt-2 border-t border-gray-100">
                <label className="block text-xs font-bold text-gray-400 uppercase tracking-wider">
                  Test Scenarios
                </label>

                {/* Simulate Transcript Q&A */}
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={simulateInterview}
                    onChange={(e) => {
                      setSimulateInterview(e.target.checked);
                      if (!e.target.checked) {
                        setSimulateVideo(false);
                        setLiveInterview(false);
                      }
                    }}
                    className="mt-1 w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                  />
                  <div>
                    <span className="text-sm font-semibold text-gray-800 block">
                      Simulate Interview Q&A
                    </span>
                    <span className="text-xs text-gray-500">
                      Creates mock session transcripts in database. Fast, does not join WebRTC.
                    </span>
                  </div>
                </label>

                {/* Simulate Video Proctoring */}
                <label
                  className={`flex items-start gap-3 cursor-pointer ${
                    !simulateInterview && 'opacity-50 pointer-events-none'
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={simulateVideo}
                    disabled={!simulateInterview}
                    onChange={(e) => setSimulateVideo(e.target.checked)}
                    className="mt-1 w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                  />
                  <div>
                    <span className="text-sm font-semibold text-gray-800 block">
                      Simulate Video Proctoring
                    </span>
                    <span className="text-xs text-gray-500">
                      Inserts artificial camera status checks and proctoring flag warnings.
                    </span>
                  </div>
                </label>

                {/* Join Live WebRTC Room */}
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={liveInterview}
                    onChange={(e) => {
                      setLiveInterview(e.target.checked);
                      if (e.target.checked) {
                        setSimulateInterview(true);
                      }
                    }}
                    className="mt-1 w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                  />
                  <div>
                    <span className="text-sm font-semibold text-gray-800 block flex items-center gap-1.5">
                      Live WebRTC Interview
                      <span className="px-1.5 py-0.5 rounded-md text-[10px] font-bold bg-amber-100 text-amber-800 border border-amber-200">
                        API Cost
                      </span>
                    </span>
                    <span className="text-xs text-gray-500">
                      Joins real LiveKit rooms, publishing synthetic video & TTS speaking audio via OpenAI.
                    </span>
                  </div>
                </label>
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={launchLoading || activeLogId != null}
                className="w-full inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl text-white bg-blue-600 hover:bg-blue-700 transition shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed font-semibold text-sm select-none"
              >
                {launchLoading ? (
                  <>
                    <RefreshCw className="w-4 h-4 animate-spin" />
                    Launching...
                  </>
                ) : (
                  <>
                    <Play className="w-4 h-4" />
                    Launch Load Test
                  </>
                )}
              </button>
              {activeLogId && (
                <p className="text-xs text-center text-amber-600 font-medium">
                  ⚠️ A load test is currently running. Please wait or cancel it to start another.
                </p>
              )}
            </form>
          </div>
        </div>

        {/* Right Side - Logs & Statistics */}
        <div className="lg:col-span-2 space-y-6">
          {/* Active Log Panel / Report */}
          {selectedLog ? (
            <div className="bg-white rounded-2xl border border-gray-200/80 shadow-sm overflow-hidden p-6 space-y-6">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 pb-4 border-b border-gray-100">
                <div>
                  <div className="flex items-center gap-2.5">
                    <h3 className="text-lg font-bold text-gray-900">
                      Load Test Details
                    </h3>
                    <span
                      className={`px-2.5 py-0.5 rounded-full text-xs font-semibold border ${getStatusColor(
                        selectedLog.status
                      )}`}
                    >
                      {selectedLog.status}
                    </span>
                  </div>
                  <p className="text-xs text-gray-500 mt-1 font-mono">
                    ID: {selectedLog._id} · Started: {formatDate(selectedLog.startedAt)}
                  </p>
                </div>
                {/* Cancel Button */}
                {selectedLog.isRunning && (
                  <button
                    type="button"
                    onClick={() => handleCancelTest(selectedLog._id)}
                    disabled={cancelLoading}
                    className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-semibold border border-red-200 bg-red-50 text-red-700 hover:bg-red-100 active:scale-95 transition disabled:opacity-50 shrink-0"
                  >
                    <Square className="w-4 h-4" />
                    {cancelLoading ? 'Cancelling...' : 'Cancel Test'}
                  </button>
                )}
              </div>

              {/* Overall Summary Cards */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                <div className="bg-gray-50 border border-gray-100 rounded-xl p-4 text-center">
                  <p className="text-2xl font-black text-gray-900">
                    {selectedLog.successCount} / {selectedLog.candidateCount}
                  </p>
                  <p className="text-[10px] font-bold text-gray-500 uppercase tracking-wide mt-1">
                    Succeeded / Total
                  </p>
                </div>
                <div className="bg-gray-50 border border-gray-100 rounded-xl p-4 text-center">
                  <p
                    className={`text-2xl font-black ${
                      selectedLog.successRate >= 90
                        ? 'text-emerald-600'
                        : selectedLog.successRate >= 50
                          ? 'text-amber-600'
                          : 'text-rose-600'
                    }`}
                  >
                    {selectedLog.successRate}%
                  </p>
                  <p className="text-[10px] font-bold text-gray-500 uppercase tracking-wide mt-1">
                    Success Rate
                  </p>
                </div>
                <div className="bg-gray-50 border border-gray-100 rounded-xl p-4 text-center font-mono">
                  <p className="text-2xl font-black text-gray-950">
                    {formatTime(selectedLog.overallTimeMs)}
                  </p>
                  <p className="text-[10px] font-bold text-gray-500 uppercase tracking-wide mt-1 font-sans">
                    Overall Duration
                  </p>
                </div>
                <div className="bg-gray-50 border border-gray-100 rounded-xl p-4 text-center font-mono">
                  <p className="text-2xl font-black text-gray-950">
                    {formatTime(selectedLog.avgTotalTimeMs)}
                  </p>
                  <p className="text-[10px] font-bold text-gray-500 uppercase tracking-wide mt-1 font-sans">
                    Avg Candidate Time
                  </p>
                </div>
              </div>

              {/* Resource Utilization summary card */}
              <div className="border border-gray-200/60 rounded-xl bg-gray-50/50 p-4 grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <p className="text-[10px] font-bold text-gray-500 uppercase tracking-wide flex items-center gap-1">
                    <Cpu className="w-3.5 h-3.5 text-blue-500" /> Peak CPU Load
                  </p>
                  <p className="text-xl font-bold text-gray-900 mt-0.5">
                    {selectedLog.peakCpu != null ? `${selectedLog.peakCpu}%` : '—'}
                  </p>
                </div>
                <div>
                  <p className="text-[10px] font-bold text-gray-500 uppercase tracking-wide flex items-center gap-1">
                    <Cpu className="w-3.5 h-3.5 text-blue-500" /> Avg CPU Load
                  </p>
                  <p className="text-xl font-bold text-gray-900 mt-0.5">
                    {selectedLog.avgCpu != null ? `${selectedLog.avgCpu}%` : '—'}
                  </p>
                </div>
                <div>
                  <p className="text-[10px] font-bold text-gray-500 uppercase tracking-wide flex items-center gap-1">
                    <Database className="w-3.5 h-3.5 text-purple-500" /> Peak Memory
                  </p>
                  <p className="text-xl font-bold text-gray-900 mt-0.5">
                    {selectedLog.peakRam != null
                      ? `${(selectedLog.peakRam / 1024).toFixed(1)} GB`
                      : '—'}
                    {selectedLog.systemTotalRam && (
                      <span className="text-xs text-gray-500 font-normal">
                        {' '}
                        / {(selectedLog.systemTotalRam / 1024).toFixed(0)} GB
                      </span>
                    )}
                  </p>
                </div>
                <div>
                  <p className="text-[10px] font-bold text-gray-500 uppercase tracking-wide flex items-center gap-1">
                    <Database className="w-3.5 h-3.5 text-purple-500" /> Avg Memory
                  </p>
                  <p className="text-xl font-bold text-gray-900 mt-0.5">
                    {selectedLog.avgRam != null ? `${(selectedLog.avgRam / 1024).toFixed(1)} GB` : '—'}
                  </p>
                </div>
              </div>

              {/* Resource Usage Charts */}
              {chartData.length > 0 && (
                <div>
                  <h4 className="text-sm font-bold text-gray-800 mb-3 flex items-center gap-1.5">
                    <Cpu className="w-4 h-4 text-gray-500" />
                    System Resource Usage Over Time
                  </h4>
                  <div className="h-64 border border-gray-200/60 rounded-xl p-4 bg-white shadow-inner">
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={chartData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                        <defs>
                          <linearGradient id="colorCpu" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.4} />
                            <stop offset="95%" stopColor="#3b82f6" stopOpacity={0.01} />
                          </linearGradient>
                          <linearGradient id="colorRam" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#8b5cf6" stopOpacity={0.4} />
                            <stop offset="95%" stopColor="#8b5cf6" stopOpacity={0.01} />
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                        <XAxis dataKey="time" stroke="#9ca3af" fontSize={10} tickLine={false} />
                        <YAxis stroke="#9ca3af" domain={[0, 100]} fontSize={10} tickLine={false} />
                        <ChartTooltip
                          content={({ active, payload }) => {
                            if (active && payload && payload.length) {
                              return (
                                <div className="bg-gray-900 text-white rounded-lg p-3 text-xs shadow border border-gray-800 flex flex-col gap-1 font-mono">
                                  <p className="font-sans font-bold text-gray-400">Offset: {payload[0].payload.time}</p>
                                  <p className="text-blue-400">CPU Usage: {payload[0].value}%</p>
                                  <p className="text-purple-400">RAM Usage: {payload[1].value}% ({payload[1].payload.ramRaw} GB)</p>
                                </div>
                              );
                            }
                            return null;
                          }}
                        />
                        <Legend iconType="circle" iconSize={8} />
                        <Area
                          type="monotone"
                          dataKey="CPU"
                          stroke="#3b82f6"
                          strokeWidth={2}
                          fillOpacity={1}
                          fill="url(#colorCpu)"
                          name="CPU (%)"
                        />
                        <Area
                          type="monotone"
                          dataKey="RAM"
                          stroke="#8b5cf6"
                          strokeWidth={2}
                          fillOpacity={1}
                          fill="url(#colorRam)"
                          name="RAM (%)"
                        />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              )}

              {/* Steps Matrix Table */}
              {selectedLog.results && selectedLog.results.length > 0 && (
                <div>
                  <h4 className="text-sm font-bold text-gray-800 mb-3 flex items-center gap-1.5">
                    <Activity className="w-4 h-4 text-gray-500" />
                    Candidate Completion Step Matrix
                  </h4>
                  <div className="overflow-x-auto border border-gray-200/60 rounded-xl">
                    <table className="min-w-full text-left text-xs bg-white">
                      <thead className="border-b border-gray-200 bg-gray-50 text-gray-500 font-bold">
                        <tr>
                          <th className="py-2.5 pl-4 pr-2 font-medium w-12 text-center">#</th>
                          <th className="py-2.5 px-3 font-medium">Candidate Name</th>
                          <th className="py-2.5 px-3 font-medium text-center">ApplyInfo</th>
                          <th className="py-2.5 px-3 font-medium text-center">BasicApply</th>
                          <th className="py-2.5 px-3 font-medium text-center">Resume</th>
                          <th className="py-2.5 px-3 font-medium text-center">LK Token</th>
                          <th className="py-2.5 px-3 font-medium text-center">Interview</th>
                          <th className="py-2.5 pl-3 pr-4 font-medium text-right">Total Time</th>
                        </tr>
                      </thead>
                      <tbody>
                        {selectedLog.results.map((res) => (
                          <tr key={res.index} className="border-b border-gray-50 last:border-0 hover:bg-gray-50/50">
                            <td className="py-2.5 pl-4 pr-2 text-center font-mono font-semibold text-gray-500">{res.index}</td>
                            <td className="py-2.5 px-3 font-semibold text-gray-900">
                              <div>
                                <p>{res.name}</p>
                                {res.error && (
                                  <p className="text-[10px] font-medium text-rose-600 mt-0.5 max-w-[200px] truncate" title={res.error}>
                                    Error: {res.error}
                                  </p>
                                )}
                              </div>
                            </td>
                            <td className="py-2.5 px-3 text-center">{getStepIcon(res.steps?.applyInfo)}</td>
                            <td className="py-2.5 px-3 text-center">{getStepIcon(res.steps?.basicApply)}</td>
                            <td className="py-2.5 px-3 text-center">{getStepIcon(res.steps?.apply)}</td>
                            <td className="py-2.5 px-3 text-center">{getStepIcon(res.steps?.livekitToken)}</td>
                            <td className="py-2.5 px-3 text-center">{getStepIcon(res.steps?.interviewSim)}</td>
                            <td className="py-2.5 pl-3 pr-4 text-right font-mono text-gray-600 font-medium">
                              {formatTime(res.timings?.total)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Terminal Logs panel */}
              <div>
                <h4 className="text-sm font-bold text-gray-800 mb-2.5 flex items-center gap-1.5">
                  <Terminal className="w-4 h-4 text-gray-500" />
                  Terminal Log Console
                </h4>
                <div className="bg-gray-950 text-gray-200 rounded-xl p-4 font-mono text-xs overflow-y-auto max-h-72 border border-gray-800/80 shadow-inner flex flex-col space-y-1">
                  <pre className="whitespace-pre-wrap leading-relaxed select-text font-mono">
                    {selectedLog.terminalLogs || 'No logs captured yet.'}
                  </pre>
                  <div ref={terminalEndRef} />
                </div>
              </div>
            </div>
          ) : (
            <div className="bg-white rounded-2xl border border-gray-200/80 shadow-sm p-12 text-center text-gray-500 flex flex-col items-center justify-center">
              <Activity className="w-12 h-12 text-gray-300 mb-3 animate-pulse" />
              <p className="font-semibold text-gray-700">No Active Simulation</p>
              <p className="text-xs text-gray-400 mt-1 max-w-sm">
                Configure candidate parameters and click &quot;Launch Load Test&quot; on the left to begin testing or select a past test from the history below.
              </p>
            </div>
          )}

          {/* Load Test History */}
          <div className="bg-white rounded-2xl border border-gray-200/80 shadow-sm overflow-hidden p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-1.5">
              <Clock className="w-5 h-5 text-gray-500" />
              Load Test History
            </h3>
            {historyLoading ? (
              <div className="flex items-center justify-center py-12">
                <RefreshCw className="w-8 h-8 text-blue-500 animate-spin" />
              </div>
            ) : history.length === 0 ? (
              <p className="text-center text-gray-500 text-sm py-8">
                No past load tests found.
              </p>
            ) : (
              <div className="overflow-x-auto border border-gray-100 rounded-xl">
                <table className="min-w-full text-left text-sm bg-white">
                  <thead className="border-b border-gray-100 bg-gray-50/80 text-gray-500 font-semibold">
                    <tr>
                      <th className="py-3 pl-4 pr-2">Date/Time</th>
                      <th className="py-3 px-3">Screening</th>
                      <th className="py-3 px-3 text-center">Load</th>
                      <th className="py-3 px-3 text-center">Success Rate</th>
                      <th className="py-3 px-3">Status</th>
                      <th className="py-3 pl-2 pr-4 text-center">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {history.map((log) => (
                      <tr
                        key={log._id}
                        onClick={() => fetchLogDetails(log._id)}
                        className={`border-b border-gray-50 last:border-0 cursor-pointer transition ${
                          selectedLog?._id === log._id ? 'bg-blue-50/40' : 'hover:bg-gray-50/70'
                        }`}
                      >
                        <td className="py-3 pl-4 pr-2 text-xs font-medium text-gray-500">
                          {formatDate(log.createdAt)}
                        </td>
                        <td className="py-3 px-3 font-semibold text-gray-900 truncate max-w-[150px]">
                          {log.screeningId?.name || 'Deleted Screening'}
                        </td>
                        <td className="py-3 px-3 text-center font-bold text-gray-700">
                          {log.candidateCount}
                        </td>
                        <td className="py-3 px-3 text-center font-semibold text-gray-800">
                          <span
                            className={
                              log.status === 'cancelled'
                                ? 'text-gray-500'
                                : log.successRate >= 90
                                  ? 'text-emerald-600'
                                  : log.successRate >= 50
                                    ? 'text-amber-600'
                                    : 'text-rose-600'
                            }
                          >
                            {log.status === 'cancelled' ? '—' : `${log.successRate}%`}
                          </span>
                        </td>
                        <td className="py-3 px-3">
                          <span
                            className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold border ${getStatusColor(
                              log.status
                            )}`}
                          >
                            {log.status}
                          </span>
                        </td>
                        <td className="py-3 pl-2 pr-4 text-center">
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              fetchLogDetails(log._id);
                            }}
                            className="inline-flex items-center gap-1 text-xs font-semibold text-blue-600 hover:text-blue-800 hover:underline"
                          >
                            <Eye className="w-3.5 h-3.5" />
                            View
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default InternalLoadTest;
